/**
 * Auto-route label managers from creator.{tenant} to labels.{tenant}.
 *
 * Global middleware (fires on EVERY navigation, not just pages that
 * opt in via `middleware: 'creator'`). The named creator.ts gate
 * accepts label_managers as creator-tier users, but doesn't redirect
 * them away from the creator subdomain — they'd render the creator-
 * branded panel even though they belong on labels.{tenant}.
 *
 * Triggered scope:
 *   - URL hostname starts with `creator.`
 *   - User is logged in
 *   - User is a label_manager (formalized role OR legacy
 *     `creator + canCreateLabels=true`, sub-users excluded)
 *
 * Path is preserved across the hop so creator.{tenant}/distribution
 * → labels.{tenant}/distribution (the labels-side route map points
 * the same path back to the creator page tree internally — no
 * broken deep links).
 */
export default defineNuxtRouteMiddleware((to) => {
    const url = useRequestURL()
    // Dev-only, and only once this middleware is on a host it could act on.
    //
    // It used to announce itself before the host check, twice per navigation
    // (SSR + CSR) — so browsing the admin panel, where this can never do
    // anything, produced four lines of "FIRE" / "skip: not on creator.*" for
    // every page and buried whatever the developer was actually looking at.
    // Silence is the correct output for a middleware that does not apply.
    const debug = import.meta.dev
    const where = typeof window === 'undefined' ? 'ssr' : 'csr'

    if (!url.hostname.startsWith('creator.')) return

    const { user } = useAuth()
    const u = user.value
    if (!u) return

    // Admins stay on creator.* — full creator features on top of admin.
    if (u.role === 'admin') return

    const role = u.role
    // Trust the explicit role: if an admin or Stripe webhook set
    // role=label_manager, the user goes to labels.* regardless of any
    // legacy parentCreator value. The legacy `canCreateLabels` fallback
    // does still require !parentCreator so a sub-user with an old plan
    // flag doesn't get incorrectly redirected.
    const isLabelManager =
        role === 'label_manager'
        || (role === 'creator' && u.canCreateLabels === true && !u.parentCreator)

    if (!isLabelManager) {
        // Kept: on creator.* with a user loaded, "why did this NOT redirect"
        // is a real question. The lines above answered one nobody asked.
        if (debug) console.log(`[labels-redirect ${where}] staying: role=${u.role} canCreateLabels=${u.canCreateLabels} parentCreator=${u.parentCreator || 'null'}`)
        return
    }

    const mainDomain = url.hostname.replace('creator.', '')
    const protocol = url.protocol
    const port = url.port ? `:${url.port}` : ''
    const target = `${protocol}//labels.${mainDomain}${port}${to.fullPath}`
    if (debug) console.log(`[labels-redirect ${where}] REDIRECTING to ${target}`)
    return navigateTo(target, { external: true, replace: true })
})
